import { useCallback, useEffect } from 'react';
import { Outlet, ScrollRestoration } from 'react-router-dom';

import { AppModals } from './App.modals';
import { AppLateInit } from './AppLateInit';

import { initializeChatApp, MessengerProvider } from '@speeki/firebase-chat';
import { resolveEnv } from '@speeki/frontend-utils';
import { ToastNotification } from '@speeki/global-ui-components';

import { useChatRoomUploadFiles } from '@utils/hooks/useChatRoomUploadFiles';

import { APIService } from '@services/ApiService';
import { useCheckIsLoggedIn } from '@services/AuthService/useCheckIsLoggedIn';
import { useTranslations } from '@services/TranslationsService/useTranslations';

import { useReactiveVar } from '@apollo/client';
import { initLoadingVar } from '@boot/reactiveVariables';
import { SkeletonDashboardPage } from '@domains/dashboard/DashboardPage.skeleton';

import { AppWrapper } from './App.styled';

const config = initializeChatApp({
    emulator:
        resolveEnv('REACT_APP_FIREBASE_EMULATOR_ENABLED', '0') === '1'
            ? {
                  auth: {
                      url: resolveEnv(
                          'REACT_APP_FIREBASE_AUTH_EMULATOR_URL',
                          'http://localhost:9099/',
                      ),
                  },
                  firestore: {
                      host: resolveEnv(
                          'REACT_APP_FIRESTORE_EMULATOR_HOST',
                          'localhost',
                      ),
                      port: Number(
                          resolveEnv(
                              'REACT_APP_FIRESTORE_EMULATOR_PORT',
                              '8080',
                          ),
                      ),
                  },
              }
            : undefined,
    fbConfigs: {
        primary: {
            cloudFunctionsEnvironment: resolveEnv(
                'REACT_APP_CLOUD_FUNCTIONS_ENV',
            ),
            config: resolveEnv('REACT_APP_FIREBASE_CONFIG'),
            firestoreEnvironment: resolveEnv('REACT_APP_FIRESTORE_CHAT_ENV'),
        },
    },
    logs: false,
    selectedFirebaseConfig: 'primary',
});

export const App = () => {
    const { fileUpload } = useChatRoomUploadFiles();
    const { checkIsLoggedIn, userData } = useCheckIsLoggedIn();
    const { currentLanguage, getTranslations } = useTranslations();
    const initLoading = useReactiveVar(initLoadingVar);

    const initApp = useCallback(async () => {
        try {
            await new APIService().initialize();
            await checkIsLoggedIn();
            await getTranslations({
                variables: {
                    targetLanguage: currentLanguage,
                },
            });
        } finally {
            initLoadingVar(false);
        }
    }, []);

    useEffect(() => {
        void initApp();
    }, []);

    return (
        <MessengerProvider
            config={config}
            requests={{
                fileUploadMutation: fileUpload,
            }}
            // @ts-expect-error TODO: Update types in package
            speekiUser={
                userData?.uuid
                    ? {
                          uuid: userData.uuid,
                          firstName: userData.firstName || undefined,
                          lastName: userData.lastName || undefined,
                          type: userData.type,
                      }
                    : undefined
            }
        >
            <AppWrapper>
                {initLoading ? (
                    <SkeletonDashboardPage />
                ) : (
                    <AppLateInit>
                        <ScrollRestoration />
                        <Outlet />
                        <AppModals />
                    </AppLateInit>
                )}
            </AppWrapper>
            <ToastNotification />
        </MessengerProvider>
    );
};
